/**
 * ManagerOperationsPanel — the manager's read-only operational triage strip
 * (FULL-0158 / PROD-UX-072).
 *
 * Composes four existing bounded projections — team queue summary, per-employee workload, the
 * pending-decision queue and recent failures — into one manager view whose every count and row
 * links to the run (the product's full audit view) or to the owning surface. It re-implements no
 * approval path: this panel renders no approve/reject control, calls no mutating endpoint and
 * grants no authority. Decisions remain on `/approval-center` and the run page, behind the
 * existing `agents_approve` gate and the server's policy checks.
 *
 * TRUTHFULNESS RULES (pinned by the focused tests):
 *   - a source that failed reads "unknown" and offers a retry; it never renders zeros;
 *   - every section states its source and the time this client loaded it, and a refresh failure
 *     keeps the held payload while labelling it as not-current;
 *   - a missing item timestamp reads "not reported", never a guessed wait;
 *   - failed work is labelled `לא הושלם`, and an empty queue never implies that all work is
 *     complete or verified.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi } from "../services/api";
import { classifySource, SOURCE_STATES } from "./statusVocabulary";
import {
  buildFailureRows,
  buildPendingRows,
  buildQueueCards,
  buildTeamLine,
  buildWorkloadRows,
  OPERATIONS_ROW_LIMIT,
} from "./managerOperationsModel";

const REFRESH_MS = 30_000;

function fmtLoadedAt(ms) {
  if (!ms) return null;
  return new Date(ms).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

function SourceLine({ label, query, testId }) {
  const state = classifySource(query);
  const loaded = fmtLoadedAt(state.since);
  if (state.key === SOURCE_STATES.failed) {
    return (
      <p
        role="alert"
        data-testid={testId}
        className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        נתוני {label} לא נטענו — המצב אינו ידוע.{" "}
        <button type="button" onClick={() => query.refetch()} className="underline">
          נסה שוב
        </button>
      </p>
    );
  }
  if (state.key === SOURCE_STATES.loading) {
    return (
      <p role="status" data-testid={testId} className="text-xs text-gray-400">
        טוען {label}…
      </p>
    );
  }
  return (
    <p
      data-testid={testId}
      className={`text-[11px] ${state.key === SOURCE_STATES.stale ? "text-amber-700" : "text-gray-400"}`}
    >
      מקור: {label} · נטען {loaded || "במועד לא ידוע"}
      {state.key === SOURCE_STATES.stale
        ? " · הרענון האחרון נכשל — הנתון אינו מוצג כעדכני"
        : ""}
    </p>
  );
}

function Section({ title, count, children, testId }) {
  return (
    <section aria-label={title} data-testid={testId} className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {count != null && <span className="text-xs text-gray-400">{count}</span>}
      </div>
      {children}
    </section>
  );
}

function QueueCard({ card }) {
  const unknown = card.value == null;
  return (
    <Link
      to={card.to}
      data-testid={`manager-ops-queue-${card.key}`}
      aria-label={`${card.label}: ${unknown ? "לא ידוע" : card.value}`}
      className="bg-white rounded-xl border shadow-sm px-3 py-2.5 hover:border-blue-300 block"
    >
      <div className={`text-2xl font-semibold ${unknown ? "text-gray-400" : card.tone}`}>
        {unknown ? "לא ידוע" : card.value}
      </div>
      <div className="text-xs text-gray-500">{card.label}</div>
      <div className="text-[11px] text-gray-400">{card.hint}</div>
    </Link>
  );
}

function CountCell({ value, to, label }) {
  if (value == null) return <span className="text-gray-400">לא ידוע</span>;
  if (value === 0) return <span className="text-gray-400">0</span>;
  return (
    <Link to={to} aria-label={`${label}: ${value}`} className="text-blue-700 hover:underline">
      {value}
    </Link>
  );
}

function PendingRow({ row }) {
  return (
    <li data-testid={`manager-ops-pending-${row.key}`} className="rounded-lg border border-orange-100 bg-orange-50/50 p-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="text-sm font-medium text-gray-800">{`אחראי: ${row.employeeName}`}</span>
        <span className="px-1.5 py-0.5 rounded-full border border-orange-200 bg-white text-orange-700">
          {row.statusLabel}
        </span>
        <span className="mr-auto text-gray-500">{row.waitAge}</span>
      </div>
      <div className="text-xs text-gray-600">
        {row.ticketTo ? (
          <Link to={row.ticketTo} className="text-blue-700 hover:underline">
            {row.ticketLabel}
          </Link>
        ) : (
          <span>{row.ticketLabel}</span>
        )}
        {row.ticketTitle ? ` · ${row.ticketTitle}` : ""}
      </div>
      <div className="text-xs text-gray-600">
        {row.actionType || row.actionDescription ? (
          <>
            <span className="text-gray-500">פעולה מוצעת: </span>
            {row.actionType || "פעולה"}
            {row.actionDescription ? ` — ${row.actionDescription}` : ""}
            {row.riskLabel ? ` · ${row.riskLabel}` : ""}
          </>
        ) : (
          <span className="text-gray-500">לא נרשמה פעולה מוצעת — פרטי ההחלטה נמצאים בריצה.</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
        <span>
          המתנה החלה: {row.waitStartedLabel || "המועד לא דווח"}
        </span>
        {row.runTo && (
          <Link to={row.runTo} className="text-blue-700 hover:underline">
            ריצה וראיות ←
          </Link>
        )}
      </div>
    </li>
  );
}

function FailureRow({ row }) {
  return (
    <li data-testid={`manager-ops-failure-${row.key}`} className="rounded-lg border border-red-100 bg-red-50/50 p-2.5 space-y-1">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className="text-sm font-medium text-gray-800">{`אחראי: ${row.employeeName}`}</span>
        <span className="px-1.5 py-0.5 rounded-full border border-red-200 bg-white text-red-700">
          {row.completionLabel}
        </span>
        <span className="text-gray-500">{row.statusLabel}</span>
        <span className="mr-auto text-gray-500">
          נכשל: {row.failedAtLabel || "המועד לא דווח"}
        </span>
      </div>
      <div className="text-xs text-gray-600">
        {row.ticketTo ? (
          <Link to={row.ticketTo} className="text-blue-700 hover:underline">
            {row.ticketLabel}
          </Link>
        ) : (
          <span>{row.ticketLabel}</span>
        )}
        {row.ticketTitle ? ` · ${row.ticketTitle}` : ""}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
        <span className="min-w-0 truncate" title={row.summary || undefined}>
          {row.summary || "לא תועדה סיבת כשל בריצה."}
        </span>
        {row.runTo && (
          <Link to={row.runTo} className="text-blue-700 hover:underline whitespace-nowrap">
            ריצה וראיות ←
          </Link>
        )}
      </div>
    </li>
  );
}

function WorkloadTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label="עומס לפי עובד">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs">
            <th scope="col" className="text-right px-2 py-1.5 font-medium">אחראי</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">עבודה נוכחית</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">פעילות</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">ממתין לסקירה</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">ממתין לאישור</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">כשלים</th>
            <th scope="col" className="text-right px-2 py-1.5 font-medium">ראיות</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-testid={`manager-ops-workload-${row.key}`} className="border-t border-gray-100">
              <td className="px-2 py-2 text-xs text-gray-700 whitespace-nowrap">
                {`אחראי: ${row.employeeName}`}
                {!row.enabled && <span className="mr-1 text-gray-400"> · מושבת</span>}
              </td>
              <td className="px-2 py-2 text-xs text-gray-600">
                {row.hasCurrentRun ? (
                  <span>
                    {row.currentTicketTo ? (
                      <Link to={row.currentTicketTo} className="text-blue-700 hover:underline">
                        {row.currentTicketLabel}
                      </Link>
                    ) : (
                      <Link to={row.currentRunTo} className="text-blue-700 hover:underline">
                        ריצה פעילה
                      </Link>
                    )}
                    {row.currentTicketTitle ? ` · ${row.currentTicketTitle}` : ""}
                  </span>
                ) : row.active != null && row.active > 0 ? (
                  <span className="text-amber-700">ריצה פעילה ללא מזהה — המצב לא ידוע</span>
                ) : row.active === 0 ? (
                  <span className="text-gray-400">אין ריצה פעילה</span>
                ) : (
                  <span className="text-gray-400">לא ידוע</span>
                )}
              </td>
              <td className="px-2 py-2 text-xs">
                <CountCell value={row.active} to={row.runsTo} label={`פעילות אצל ${row.employeeName}`} />
              </td>
              <td className="px-2 py-2 text-xs">
                <CountCell value={row.review} to={row.reviewTo} label={`ממתין לסקירה אצל ${row.employeeName}`} />
              </td>
              <td className="px-2 py-2 text-xs">
                <CountCell value={row.approval} to={row.approvalTo} label={`ממתין לאישור אצל ${row.employeeName}`} />
              </td>
              <td className="px-2 py-2 text-xs">
                <CountCell value={row.failures} to={row.failuresTo} label={`כשלים אצל ${row.employeeName}`} />
              </td>
              <td className="px-2 py-2 text-xs whitespace-nowrap">
                <Link
                  to={row.currentRunTo || row.runsTo}
                  className="text-blue-700 hover:underline"
                >
                  {row.currentRunTo ? "ריצה וראיות" : "יומן ריצות"}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ManagerOperationsPanel({ canView, companyId = "", companyName = null }) {
  const enabled = Boolean(canView);
  const scope = companyId ? { company_id: companyId } : {};
  const scopeKey = companyId || "all";

  const teamQ = useQuery({
    queryKey: ["manager-operations", "team", scopeKey],
    queryFn: () => agentRunsApi.teamQueueSummary(scope),
    enabled,
    refetchInterval: REFRESH_MS,
    staleTime: 15_000,
  });
  const workloadQ = useQuery({
    queryKey: ["manager-operations", "workload", scopeKey],
    queryFn: () => agentRunsApi.workloadSummary(scope),
    enabled,
    refetchInterval: REFRESH_MS,
    staleTime: 15_000,
  });
  const pendingQ = useQuery({
    queryKey: ["manager-operations", "pending", scopeKey],
    queryFn: () => agentRunsApi.pendingApprovals(scope),
    enabled,
    refetchInterval: REFRESH_MS,
    staleTime: 15_000,
  });
  const failuresQ = useQuery({
    queryKey: ["manager-operations", "failures", scopeKey],
    queryFn: () => agentRunsApi.recentFailures({ ...scope, limit: 20 }),
    enabled,
    refetchInterval: REFRESH_MS,
    staleTime: 15_000,
  });

  if (!enabled) return null;

  const cards = buildQueueCards(teamQ.data);
  const teamLine = buildTeamLine(teamQ.data);
  const pendingRows = buildPendingRows(pendingQ.data);
  const workloadRows = buildWorkloadRows(workloadQ.data);
  const failureRows = buildFailureRows(failuresQ.data);

  const pendingTotal = pendingQ.data?.pending_count;
  const failureTotal = failuresQ.data?.failure_count;
  const failureWindow = failuresQ.data?.window_hours;

  const refresh = () => {
    teamQ.refetch();
    workloadQ.refetch();
    pendingQ.refetch();
    failuresQ.refetch();
  };

  return (
    <section
      aria-label="מצב תפעולי"
      data-testid="manager-operations"
      className="space-y-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-800">מצב תפעולי — עומס, תורים והסלמות</h2>
          <p className="text-xs text-gray-500">
            תמונת מצב לקריאה בלבד. כל שורה מקשרת לריצה ולעדויות הביקורת שלה; אין כאן אישור,
            הקצאה או ביצוע. ההחלטה עצמה מתבצעת רק בשרת, במסלול המדיניות הקיים.
            {companyId ? ` מסונן ללקוח: ${companyName || companyId}.` : " כל הלקוחות."}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
        >
          רענן מצב תפעולי
        </button>
      </div>

      <section aria-label="סיכום תור הצוות" className="space-y-1.5">
        <SourceLine label="סיכום תור הצוות" query={teamQ} testId="manager-ops-source-team" />
        {cards.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {cards.map((card) => (
              <QueueCard key={card.key} card={card} />
            ))}
          </div>
        )}
        {teamLine && (
          <p data-testid="manager-ops-team-line" className="text-[11px] text-gray-500">
            סה״כ עובדים: {teamLine.total == null ? "לא ידוע" : teamLine.total}
            {" · "}זמינים: {teamLine.available == null ? "לא ידוע" : teamLine.available}
            {" · "}עסוקים: {teamLine.busy == null ? "לא ידוע" : teamLine.busy}
          </p>
        )}
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <Section
          title="תור החלטות — הממתין הוותיק ביותר ראשון"
          count={
            pendingQ.data
              ? `${pendingTotal ?? 0} החלטות · ${pendingQ.data.agents_waiting_count ?? 0} עובדים`
              : null
          }
          testId="manager-ops-pending"
        >
          <SourceLine label="תור ההחלטות" query={pendingQ} testId="manager-ops-source-pending" />
          {pendingQ.data != null && pendingRows.length === 0 && (
            <p className="text-sm text-gray-400">
              אין החלטות הממתינות. אין בכך אישור שכל העבודה הושלמה או אומתה — בדקו את הכשלים
              האחרונים ואת יומן הריצות.
            </p>
          )}
          {pendingRows.length > 0 && (
            <ul className="space-y-1.5">
              {pendingRows.slice(0, OPERATIONS_ROW_LIMIT).map((row) => (
                <PendingRow key={row.key} row={row} />
              ))}
            </ul>
          )}
          {pendingRows.length > OPERATIONS_ROW_LIMIT && (
            <Link to="/approval-center" className="inline-block text-xs text-blue-700 hover:underline">
              לכל ההחלטות ({pendingTotal}) ←
            </Link>
          )}
          {pendingRows.length > 0 && pendingRows.length <= OPERATIONS_ROW_LIMIT && (
            <Link to="/approval-center" className="inline-block text-xs text-blue-700 hover:underline">
              פתח את מרכז האישורים ←
            </Link>
          )}
        </Section>

        <Section
          title="הסלמות — ריצות שנכשלו"
          count={
            failuresQ.data
              ? `${failureTotal ?? 0} ריצות${failureWindow != null ? ` · ${failureWindow} שעות` : ""}`
              : null
          }
          testId="manager-ops-failures"
        >
          <SourceLine label="הכשלים האחרונים" query={failuresQ} testId="manager-ops-source-failures" />
          {failuresQ.data != null && failureRows.length === 0 && (
            <p className="text-sm text-gray-400">
              לא דווחו ריצות שנכשלו בחלון הזמן הזה. אין בכך אישור שכל העבודה הושלמה או אומתה.
            </p>
          )}
          {failureRows.length > 0 && (
            <ul className="space-y-1.5">
              {failureRows.slice(0, OPERATIONS_ROW_LIMIT).map((row) => (
                <FailureRow key={row.key} row={row} />
              ))}
            </ul>
          )}
          {failureRows.length > OPERATIONS_ROW_LIMIT && (
            <Link to="/agent-runs?status=failed" className="inline-block text-xs text-blue-700 hover:underline">
              לכל הריצות שנכשלו ({failureTotal}) ←
            </Link>
          )}
          {failureRows.length > 0 && failureRows.length <= OPERATIONS_ROW_LIMIT && (
            <Link to="/agent-runs?status=failed" className="inline-block text-xs text-blue-700 hover:underline">
              פתח את כל הריצות שנכשלו ←
            </Link>
          )}
        </Section>
      </div>

      <Section
        title="עומס לפי עובד — בעלים וכתובת הראיות"
        count={workloadQ.data ? `${workloadRows.length} עובדים` : null}
        testId="manager-ops-workload"
      >
        <SourceLine label="עומס העובדים" query={workloadQ} testId="manager-ops-source-workload" />
        {workloadQ.data != null && workloadRows.length === 0 && (
          <p className="text-sm text-gray-400">לא דווחו עובדים בפרויקט העומס.</p>
        )}
        {workloadRows.length > 0 && <WorkloadTable rows={workloadRows} />}
        <p role="note" className="text-[11px] text-gray-400">
          מוצגים רק נתונים שהשרת דיווח. אישור או דחייה של פעולה מתבצעים במרכז האישורים או בעמוד
          הריצה, תחת הרשאת agents_approve ובדיקות המדיניות של השרת — לא מהמסך הזה.
        </p>
      </Section>
    </section>
  );
}
